import { forwardRef, type InputHTMLAttributes, type CSSProperties } from 'react';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'style'> {
  label?: string;
  error?: string;
  helperText?: string;
  /** Render the value/text with --font-mono. Useful for IDs, hostnames, etc. */
  mono?: boolean;
  style?: CSSProperties;
}

const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, helperText, mono, style, ...rest },
  ref
) {
  const errBorder = 'oklch(0.55 0.18 25)';
  return (
    <div style={{ width: '100%' }}>
      {label && (
        <label
          style={{
            display: 'block',
            fontSize: 12.5,
            fontWeight: 500,
            color: 'var(--fg-muted, oklch(0.56 0.006 230))',
            marginBottom: 6,
            letterSpacing: '-0.005em',
          }}
        >
          {label}
        </label>
      )}
      <input
        ref={ref}
        style={{
          display: 'block',
          width: '100%',
          height: 34,
          padding: '0 12px',
          fontFamily: mono ? 'var(--font-mono)' : 'inherit',
          fontSize: 13.5,
          letterSpacing: '-0.005em',
          color: 'var(--fg, oklch(0.94 0.003 230))',
          background: 'var(--surface-2, oklch(0.14 0.012 230))',
          border: `1px solid ${error ? errBorder : 'var(--border, oklch(0.18 0.012 230))'}`,
          borderRadius: 'var(--r-2, 6px)',
          outline: 'none',
          transition: 'border-color 120ms ease, box-shadow 120ms ease',
          ...style,
        }}
        onFocus={(e) => {
          e.currentTarget.style.borderColor = error ? errBorder : 'var(--ice-500, oklch(0.66 0.150 205))';
          e.currentTarget.style.boxShadow = error
            ? `0 0 0 2px oklch(0.55 0.18 25 / 0.25)`
            : '0 0 0 2px oklch(0.66 0.150 205 / 0.25)';
        }}
        onBlur={(e) => {
          e.currentTarget.style.borderColor = error ? errBorder : 'var(--border, oklch(0.18 0.012 230))';
          e.currentTarget.style.boxShadow = 'none';
        }}
        {...rest}
      />
      {error && (
        <p style={{ marginTop: 6, fontSize: 12, color: 'oklch(0.80 0.12 25)' }}>{error}</p>
      )}
      {helperText && !error && (
        <p style={{ marginTop: 6, fontSize: 12, color: 'var(--fg-dim, oklch(0.40 0.008 230))' }}>{helperText}</p>
      )}
    </div>
  );
});

export default Input;
